import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useContent } from '@/stores/RootStore';
import { useReveal } from '@/hooks/useReveal';
import { scrollToSection } from '@/hooks/useLenis';
import SubPageHero from '@/components/SubPageHero';
import OrgChart from '@/components/OrgChart';
import Timeline from '@/components/Timeline';
import TalentValues from '@/components/TalentValues';
import IotGatewayGraphic from '@/components/IotGatewayGraphic';
import s from './Company.module.scss';

// 회사소개 = 단일 서브페이지 + 앵커 섹션(2026-08-02, 사용자 방향).
//   자식(인사말·조직도…)마다 페이지를 나누지 않고 이 한 페이지의 섹션으로 두며,
//   헤더 서브메뉴 클릭 → /company#id 로 이동 → 아래 useEffect가 Lenis로 해당 섹션에 부드럽게 스크롤.
//   디자인 기준 = evs.com/applications/corporate-events (정지 히어로 + 중앙정렬 콘텐츠 + reveal).
//   현재: '인사말' 섹션만 evstech 원문으로 완성. 나머지는 섹션 뼈대(스크롤 타깃 확보) — 추후 채움.

const GreetingSection = observer(() => {
  const { greeting } = useContent();
  const reveal = useReveal<HTMLElement>();
  return (
    <section id="greeting" className={s.section} ref={reveal}>
      <div className={s.inner}>
        <p className={`${s.eyebrow} anim-scroll`}>{greeting.eyebrow}</p>
        <h2 className={`${s.headline} anim-scroll`}>
          {greeting.headLead}
          <span className={s.accent}>{greeting.headAccent}</span>
          {greeting.headTail}
        </h2>
        <p className={`${s.lead} anim-scroll`}>{greeting.lead}</p>
        {greeting.body.map((para, i) => (
          <p key={i} className={`${s.body} anim-scroll`}>{para}</p>
        ))}
        <p className={`${s.signature} anim-scroll`}>{greeting.signature}</p>
      </div>

      {/* 철학 그래픽 = IoT Gateway 키비주얼(풀블리드). .inner 밖(섹션 직속)이라 100vw 브레이크아웃이 정확. */}
      <div className={`${s.philosophy} anim-scroll`}>
        <IotGatewayGraphic />
      </div>
    </section>
  );
});

// 조직도 — EVS 벡터 조직도(evstech 구조 재해석).
const OrgSection = () => {
  const reveal = useReveal<HTMLDivElement>();
  return (
    <section id="org" className={`${s.section} ${s.alt}`}>
      {/* reveal 대상(풀폭) — 자식 .anim-scroll 들이 진입 시 함께 노출 */}
      <div ref={reveal}>
        <div className={s.inner}>
          <p className={`${s.eyebrow} anim-scroll`}>조직도</p>
          <h2 className={`${s.headline} anim-scroll`}>조직도</h2>
        </div>
        <div className={`${s.orgWrap} anim-scroll`}>
          <OrgChart />
        </div>
      </div>
    </section>
  );
};

// 주요연혁 — EVS 모던 타임라인.
const HistorySection = () => {
  const reveal = useReveal<HTMLDivElement>();
  return (
    <section id="history" className={s.section}>
      <div ref={reveal}>
        <div className={s.inner}>
          <p className={`${s.eyebrow} anim-scroll`}>주요연혁</p>
          <h2 className={`${s.headline} anim-scroll`}>주요연혁</h2>
        </div>
        <div className={`${s.timelineWrap} anim-scroll`}>
          <Timeline />
        </div>
      </div>
    </section>
  );
};

// 인재상 — 철학 문구 + 2대 가치 카드(EVS SVG 아이콘).
const TalentSection = observer(() => {
  const { talent } = useContent();
  const reveal = useReveal<HTMLDivElement>();
  return (
    <section id="talent" className={`${s.section} ${s.alt}`}>
      <div ref={reveal}>
        <div className={s.inner}>
          <p className={`${s.eyebrow} anim-scroll`}>{talent.eyebrow}</p>
          <h2 className={`${s.headline} anim-scroll`}>인재상</h2>
          <p className={`${s.lead} anim-scroll`}>{talent.headline}</p>
          <p className={`${s.talentIdeology} anim-scroll`}>{talent.ideology}</p>
        </div>
        <div className={`${s.talentWrap} anim-scroll`}>
          <TalentValues />
        </div>
      </div>
    </section>
  );
});

// 오시는 길 — 본사·R&D센터 지도(구글맵 임베드) + 주소. 홈 Location과 동일 데이터/임베드 방식 재사용.
const LocationSection = observer(() => {
  const { location } = useContent();
  const reveal = useReveal<HTMLDivElement>();
  return (
    <section id="location" className={s.section}>
      <div ref={reveal}>
        <div className={s.inner}>
          <p className={`${s.eyebrow} anim-scroll`}>오시는 길</p>
          <h2 className={`${s.headline} anim-scroll`}>오시는 길</h2>
        </div>
        <div className={`${s.locGrid} anim-scroll`}>
          {location.offices.map((o) => {
            const src = 'mapEmbed' in o && o.mapEmbed
              ? o.mapEmbed
              : `https://maps.google.com/maps?q=${encodeURIComponent((o as { mapQuery: string }).mapQuery)}&z=16&hl=ko&output=embed`;
            return (
              <div className={s.office} key={o.label}>
                <div className={s.map}>
                  <iframe src={src} title={`${o.label} 지도`} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" />
                </div>
                <div className={s.officeInfo}>
                  <span className={s.officeLabel}>{o.label}</span>
                  <p className={s.officeAddr}>{o.address}</p>
                  <p className={s.officeTel}>Tel. {o.tel}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
});

const Company = observer(() => {
  const { companyHero } = useContent();
  const { hash } = useLocation();

  // 해시(#greeting 등)로 진입/변경 시 해당 섹션으로 부드럽게 스크롤. 해시 없으면 최상단.
  //   ★로드 중 언더슛 방지: 폰트(document.fonts.ready)까지 확정된 뒤 스크롤해야 섹션 위치가 안정됨.
  //     폰트 로드 전엔 폴백 폰트로 텍스트 높이가 달라 목표 위치가 어긋남(초기 진입 시 못 미치는 현상).
  //   scrollToSection 은 Lenis 준비까지 자체 재시도.
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const id = hash.replace('#', '');
    let cancelled = false;
    const run = () => { if (!cancelled) requestAnimationFrame(() => scrollToSection(id)); };
    if (document.fonts?.ready) document.fonts.ready.then(run);
    else run();
    return () => { cancelled = true; };
  }, [hash]);

  return (
    <main>
      {/* 현재 위치 표기 = 목업(evs.com/applications) 방식으로 히어로 상단에 HOME › 회사소개 (사용자 2026-08-02). */}
      <SubPageHero
        align="left"
        title={companyHero.slogan}
        subtitle={companyHero.sloganKo}
        image="/images/company/subpage-hero.webp"
        crumbs={[{ label: 'HOME', to: '/' }, { label: '회사소개' }, { label: '인사말·회사개요' }]}
      />
      <GreetingSection />
      <OrgSection />
      <HistorySection />
      <TalentSection />
      <LocationSection />
    </main>
  );
});

export default Company;
